import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ResearchLayout from '../../components/ResearchLayout';
import api from '../../services/api';
import Modal from '../../components/Modal';

const MedicalAdminBatchCreation = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendingBatch, setExtendingBatch] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(null);
  const [batchStatusFilter, setBatchStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    gracePeriodHours: 12
  });

  // Extend deadline form state
  const [extendFormData, setExtendFormData] = useState({
    newEndDate: '',
    extensionReason: ''
  });

  // Validation state
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = async () => {
    try {
      setLoading(true);
      // Medical admins and program admins can see ALL batches
      // Remove created_by_me filter to show universal batches
      const response = await api.get('/batches');
      setBatches(response.data.batches || []);
    } catch (err) {
      console.error('Error loading batches:', err);
      setError('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Batch name is required';
    }
    
    if (!formData.startDate) {
      errors.startDate = 'Start date is required';
    }
    
    if (!formData.endDate) {
      errors.endDate = 'End date is required';
    }
    
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      
      console.log('Validation - Start date:', formData.startDate, startDate);
      console.log('Validation - End date:', formData.endDate, endDate);
      
      if (endDate <= startDate) {
        errors.endDate = 'End date must be after start date';
      }
      
      // Check if start date is in the past (allow today)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (startDate < today) {
        errors.startDate = 'Start date cannot be in the past';
      }
    }
    
    if (formData.gracePeriodHours < 0 || formData.gracePeriodHours > 168) {
      errors.gracePeriodHours = 'Grace period must be between 0 and 168 hours';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'gracePeriodHours' ? parseInt(value) || 0 : value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Helper function to convert local date to EAT datetime (sets to 12:00 AM of the date)
  const dateToUTC = (dateString) => {
    if (!dateString) return null;
    
    // Parse the YYYY-MM-DD string and create a date at midnight in EAT (UTC+3)
    const [year, month, day] = dateString.split('-').map(Number);
    // Create date at 12:00 AM (midnight) in EAT timezone (UTC+3)
    const eatDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
    // Add 3 hours to get EAT time (midnight EAT = 9:00 PM UTC)
    eatDate.setUTCHours(eatDate.getUTCHours() + 3);
    
    return eatDate.toISOString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        start_date: dateToUTC(formData.startDate),
        end_date: dateToUTC(formData.endDate),
        grace_period_hours: formData.gracePeriodHours
      };
      
      console.log('Sending batch data:', payload); // Debug log
      
      const response = editingBatch
        ? await api.put(`/batches/${editingBatch.id}`, payload)
        : await api.post('/batches', payload);
      
      setSuccess(editingBatch ? 'Batch updated successfully!' : 'Batch created successfully!');
      setShowModal(false);
      setEditingBatch(null);
      resetForm();
      await loadBatches();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Error saving batch:', err);
      setError(err.response?.data?.error || 'Failed to save batch');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (batch) => {
    setEditingBatch(batch);
    // Convert UTC dates back to local date format for the date input fields
    const startDate = batch.start_date ? new Date(batch.start_date) : null;
    const endDate = batch.end_date ? new Date(batch.end_date) : null;
    
    setFormData({
      name: batch.name,
      description: batch.description || '',
      startDate: startDate ? startDate.toISOString().split('T')[0] : '',
      endDate: endDate ? endDate.toISOString().split('T')[0] : '',
      gracePeriodHours: batch.grace_period_hours || 12
    });
    setValidationErrors({});
    setShowModal(true);
  };

  const openExtendModal = (batch) => {
    setExtendingBatch(batch);
    setExtendFormData({
      newEndDate: '',
      extensionReason: ''
    });
    setShowExtendModal(true);
  };

  const handleExtendSubmit = async (e) => {
    e.preventDefault();
    
    if (!extendFormData.newEndDate) {
      setError('New end date is required');
      return;
    }

    if (!extendFormData.extensionReason.trim()) {
      setError('Extension reason is required');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Convert the date to proper UTC timestamp
      const utcEndDate = dateToUTC(extendFormData.newEndDate);
      await api.post(`/batches/${extendingBatch.id}/extend`, {
        extended_end_date: utcEndDate,
        extension_reason: extendFormData.extensionReason.trim()
      });
      
      setSuccess(`Batch deadline extended successfully to ${extendFormData.newEndDate}!`);
      setShowExtendModal(false);
      setExtendingBatch(null);
      await loadBatches();
      setTimeout(() => setSuccess(''), 5000);
      
    } catch (err) {
      console.error('Error extending deadline:', err);
      setError(err.response?.data?.error || 'Failed to extend deadline');
    } finally {
      setLoading(false);
    }
  };

  const handleExtendInputChange = (e) => {
    const { name, value } = e.target;
    setExtendFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const openDeleteModal = (batch) => {
    setDeletingBatch(batch);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deletingBatch) return;
    
    try {
      setLoading(true);
      setError('');
      
      await api.delete(`/batches/${deletingBatch.id}`);
      
      setSuccess(`Batch "${deletingBatch.name}" deleted successfully!`);
      setShowDeleteModal(false);
      setDeletingBatch(null);
      await loadBatches();
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Error deleting batch:', err);
      setError(err.response?.data?.error || 'Failed to delete batch');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDisable = async (batch) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.post(`/batches/${batch.id}/toggle-enabled`);
      const action = response.data.is_enabled ? 'enabled' : 'disabled';
      
      setSuccess(`Batch "${batch.name}" ${action} successfully!`);
      await loadBatches();
      setTimeout(() => setSuccess(''), 5000);
      
    } catch (err) {
      console.error('Error toggling batch status:', err);
      setError(err.response?.data?.error || 'Failed to toggle batch status');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      gracePeriodHours: 12
    });
    setValidationErrors({});
  };

  const openCreateModal = () => {
    setEditingBatch(null);
    resetForm();
    setShowModal(true);
  };

  const getBatchStatus = (batch) => {
    // Check if batch is disabled first
    if (batch.is_enabled === false) {
      return { text: 'Disabled', class: 'bg-secondary' };
    }
    if (batch.is_expired) {
      return { text: 'Expired', class: 'bg-danger' };
    } else if (batch.is_active) {
      return { text: 'Active', class: 'bg-success' };
    } else {
      return { text: 'Scheduled', class: 'bg-warning' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      // Display the date in EAT timezone (East Africa Time, UTC+3)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Africa/Nairobi'  // EAT timezone
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getTimeRemaining = (timeRemaining) => {
    if (!timeRemaining) return 'Expired';
    return timeRemaining;
  };

  // Filter batches based on search term and status filter
  const filteredBatches = batches.filter(batch => {
    // Search filter
    const matchesSearch = !searchTerm ||
      batch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (batch.description && batch.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Status filter
    let matchesStatus = true;
    if (batchStatusFilter === 'active') {
      matchesStatus = batch.is_active;
    } else if (batchStatusFilter === 'scheduled') {
      matchesStatus = !batch.is_active && !batch.is_expired;
    } else if (batchStatusFilter === 'expired') {
      matchesStatus = batch.is_expired;
    }
    
    return matchesSearch && matchesStatus;
  });

  if (loading && batches.length === 0) {
    return (
      <ResearchLayout pageTitle="Batch Management">
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading batches...</p>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout pageTitle="Batch Management">
      <div className="container-fluid">
        {/* Success/Error Messages */}
        {success && (
          <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            {success}
            <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
          </div>
        )}
        
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        {/* Header Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-primary text-white">
                <div className="row align-items-center">
                  <div className="col-md-8">
                    <h4 className="mb-0">
                      <i className="fas fa-layer-group me-2"></i>
                      Batch Management
                    </h4>
                    <small className="opacity-75">Create and manage case batches with deadlines</small>
                  </div>
                  <div className="col-md-4 text-end">
                    <button
                      className="btn btn-light btn-lg"
                      onClick={openCreateModal}
                      disabled={loading}
                    >
                      <i className="fas fa-plus me-2"></i>
                      Create New Batch
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search batches by name or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4 mb-2">
                    <select
                      className="form-select"
                      value={batchStatusFilter}
                      onChange={(e) => setBatchStatusFilter(e.target.value)}
                    >
                      <option value="all">All Batches</option>
                      <option value="active">Active Batches</option>
                      <option value="scheduled">Scheduled Batches</option>
                      <option value="expired">Expired Batches</option>
                    </select>
                  </div>
                  <div className="col-md-2 mb-2 text-end">
                    <div className="badge bg-primary fs-6">
                      {filteredBatches.length} of {batches.length}
                    </div>
                  </div>
                </div>
                
                {batchStatusFilter !== 'all' && (
                  <div className="alert alert-info mt-2 mb-0">
                    <i className="fas fa-filter me-2"></i>
                    <strong>Filter Active:</strong>
                    Showing {batchStatusFilter} batches
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Batches Grid */}
        <div className="row">
          {batches.length === 0 ? (
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <i className="fas fa-layer-group fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No Batches Created Yet</h5>
                  <p className="text-muted">
                    Create your first batch to start grouping cases with deadlines.
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={openCreateModal}
                  >
                    <i className="fas fa-plus me-2"></i>
                    Create First Batch
                  </button>
                </div>
              </div>
            </div>
          ) : filteredBatches.length === 0 ? (
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <i className="fas fa-filter fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No batches match your filter</h5>
                  <p className="text-muted">
                    Try adjusting the search criteria or status filter.
                  </p>
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => {
                      setSearchTerm('');
                      setBatchStatusFilter('all');
                    }}
                  >
                    <i className="fas fa-times me-2"></i>
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          ) : (
            filteredBatches.map((batch) => {
              const status = getBatchStatus(batch);
              return (
                <div key={batch.id} className="col-lg-6 col-xl-4 mb-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="mb-0 fw-bold">{batch.name}</h6>
                      <span className={`badge ${status.class}`}>
                        {status.text}
                      </span>
                    </div>
                    
                    <div className="card-body">
                      {/* Description */}
                      {batch.description && (
                        <p className="text-muted small mb-3">{batch.description}</p>
                      )}
                      
                      {/* Timing Information */}
                      <div className="row mb-3">
                        <div className="col-6">
                          <div className="small text-muted">Start Date</div>
                          <div className="fw-bold small">{formatDate(batch.start_date)}</div>
                        </div>
                        <div className="col-6">
                          <div className="small text-muted">End Date</div>
                          <div className="fw-bold small">
                            {formatDate(batch.extended_end_date || batch.end_date)}
                            {batch.extended_end_date && (
                              <small className="d-block text-success">
                                <i className="fas fa-clock me-1"></i>Extended
                              </small>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Time Remaining */}
                      <div className="mb-3">
                        <div className="small text-muted">Time Remaining</div>
                        <div className={`fw-bold ${batch.is_expired ? 'text-danger' : batch.is_active ? 'text-success' : 'text-warning'}`}>
                          <i className="fas fa-hourglass-half me-1"></i>
                          {getTimeRemaining(batch.time_remaining)}
                        </div>
                      </div>
                      
                      {/* Statistics */}
                      <div className="row mb-3">
                        <div className="col-6 text-center">
                          <div className="h6 mb-0 text-primary">{batch.case_count || 0}</div>
                          <small className="text-muted">Cases</small>
                        </div>
                        <div className="col-6 text-center">
                          <div className="h6 mb-0 text-info">{batch.doctor_count || 0}</div>
                          <small className="text-muted">Doctors</small>
                        </div>
                      </div>
                      
                      {/* Grace Period */}
                      <div className="mb-3">
                        <small className="text-muted">
                          <i className="fas fa-shield-alt me-1"></i>
                          {batch.grace_period_hours}h Grace Period
                        </small>
                      </div>
                      
                      {/* Created Date */}
                      <div className="small text-muted">
                        <i className="fas fa-calendar-plus me-1"></i>
                        Created: {formatDate(batch.created_at)}
                      </div>
                    </div>
                    
                    <div className="card-footer bg-light">
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary flex-fill"
                          onClick={() => handleEdit(batch)}
                        >
                          <i className="fas fa-edit me-1"></i>
                          Edit
                        </button>
                        
                        {!batch.is_expired && (
                          <button
                            className="btn btn-sm btn-outline-warning flex-fill"
                            onClick={() => openExtendModal(batch)}
                            disabled={loading}
                          >
                            <i className="fas fa-clock me-1"></i>
                            Extend
                          </button>
                        )}
                        
                        <a
                          href={`/medical-admin/assign-cases-to-batch/${batch.id}`}
                          className="btn btn-sm btn-outline-success flex-fill"
                        >
                          <i className="fas fa-users me-1"></i>
                          Assign Cases
                        </a>
                      </div>
                      
                      <div className="mt-2">
                        <a
                          href={`/medical-admin/assign-batch-to-doctors/${batch.id}`}
                          className="btn btn-sm btn-outline-info w-100"
                        >
                          <i className="fas fa-user-md me-1"></i>
                          Assign to Doctors
                        </a>
                      </div>
                      
                      <div className="mt-2">
                        <button
                          className={`btn btn-sm ${batch.is_enabled === false ? 'btn-outline-success w-100' : 'btn-outline-warning w-100'}`}
                          onClick={() => handleToggleDisable(batch)}
                          disabled={loading}
                          title={batch.is_enabled === false ? 'Enable batch for doctors' : 'Disable batch - doctors will not access cases'}
                        >
                          <i className={`fas ${batch.is_enabled === false ? 'fa-check' : 'fa-ban'} me-1`}></i>
                          {batch.is_enabled === false ? 'Enable Batch' : 'Disable Batch'}
                        </button>
                      </div>
                      
                      <div className="mt-2">
                        <button
                          className="btn btn-sm btn-outline-danger w-100"
                          onClick={() => openDeleteModal(batch)}
                          disabled={loading}
                        >
                          <i className="fas fa-trash me-1"></i>
                          Delete Batch
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Create/Edit Batch Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={editingBatch ? 'Edit Batch' : 'Create New Batch'}
          size="lg"
        >
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="name" className="form-label">
                  <i className="fas fa-tag me-1"></i>
                  Batch Name *
                </label>
                <input
                  type="text"
                  className={`form-control ${validationErrors.name ? 'is-invalid' : ''}`}
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Q1 2025 Assessment Batch"
                />
                {validationErrors.name && (
                  <div className="invalid-feedback">{validationErrors.name}</div>
                )}
              </div>
              
              <div className="col-md-6 mb-3">
                <label htmlFor="gracePeriodHours" className="form-label">
                  <i className="fas fa-shield-alt me-1"></i>
                  Grace Period (hours)
                </label>
                <input
                  type="number"
                  className={`form-control ${validationErrors.gracePeriodHours ? 'is-invalid' : ''}`}
                  id="gracePeriodHours"
                  name="gracePeriodHours"
                  value={formData.gracePeriodHours}
                  onChange={handleInputChange}
                  min="0"
                  max="168"
                />
                {validationErrors.gracePeriodHours && (
                  <div className="invalid-feedback">{validationErrors.gracePeriodHours}</div>
                )}
                <small className="text-muted">
                  Additional time doctors have after deadline (default: 12 hours)
                </small>
              </div>
            </div>
            
            <div className="mb-3">
              <label htmlFor="description" className="form-label">
                <i className="fas fa-align-left me-1"></i>
                Description
              </label>
              <textarea
                className="form-control"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                placeholder="Describe the purpose or criteria for this batch..."
              ></textarea>
            </div>
            
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="startDate" className="form-label">
                  <i className="fas fa-play me-1"></i>
                  Start Date *
                </label>
                <input
                  type="date"
                  className={`form-control ${validationErrors.startDate ? 'is-invalid' : ''}`}
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]}
                />
                {validationErrors.startDate && (
                  <div className="invalid-feedback">{validationErrors.startDate}</div>
                )}
              </div>
              
              <div className="col-md-6 mb-3">
                <label htmlFor="endDate" className="form-label">
                  <i className="fas fa-stop me-1"></i>
                  End Date *
                </label>
                <input
                  type="date"
                  className={`form-control ${validationErrors.endDate ? 'is-invalid' : ''}`}
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  min={formData.startDate || new Date().toISOString().split('T')[0]}
                />
                {validationErrors.endDate && (
                  <div className="invalid-feedback">{validationErrors.endDate}</div>
                )}
              </div>
            </div>
            
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              <strong>Important:</strong>
              Batches run from 12:00 AM (midnight) of the start date to 12:00 AM (midnight) of the end date.
              Doctors will have until the end date plus the grace period to complete cases in this batch.
              Once expired, cases will no longer be accessible to doctors, but completed work will be retained.
            </div>
            
            <div className="d-flex justify-content-end gap-2">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {editingBatch ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    {editingBatch ? 'Update Batch' : 'Create Batch'}
                  </>
                )}
              </button>
            </div>
          </form>
        </Modal>

        {/* Extend Deadline Modal */}
        <Modal
          isOpen={showExtendModal}
          onClose={() => setShowExtendModal(false)}
          title="Extend Batch Deadline"
          size="md"
        >
          {extendingBatch && (
            <div>
              <div className="alert alert-info mb-4">
                <h6 className="mb-2">
                  <i className="fas fa-info-circle me-2"></i>
                  Current Batch Details
                </h6>
                <div className="row">
                  <div className="col-sm-4">
                    <strong>Name:</strong>
                    <br />
                    <span className="text-muted">{extendingBatch.name}</span>
                  </div>
                  <div className="col-sm-4">
                    <strong>Current End:</strong>
                    <br />
                    <span className="text-muted">{formatDate(extendingBatch.end_date)}</span>
                  </div>
                  <div className="col-sm-4">
                    <strong>Time Remaining:</strong>
                    <br />
                    <span className="text-warning">{extendingBatch.time_remaining || 'Calculating...'}</span>
                  </div>
                </div>
              </div>

              <form onSubmit={handleExtendSubmit}>
                <div className="mb-3">
                  <label htmlFor="newEndDate" className="form-label">
                    <i className="fas fa-calendar-alt me-1"></i>
                    New End Date *
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="newEndDate"
                    name="newEndDate"
                    value={extendFormData.newEndDate}
                    onChange={handleExtendInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                  <small className="text-muted">
                    Batches extend to 12:00 AM (midnight) of the selected date in EAT timezone
                  </small>
                </div>

                <div className="mb-3">
                  <label htmlFor="extensionReason" className="form-label">
                    <i className="fas fa-comment me-1"></i>
                    Extension Reason *
                  </label>
                  <textarea
                    className="form-control"
                    id="extensionReason"
                    name="extensionReason"
                    value={extendFormData.extensionReason}
                    onChange={handleExtendInputChange}
                    rows="3"
                    placeholder="Explain why this deadline extension is needed..."
                    required
                  ></textarea>
                  <small className="text-muted">
                    This reason will be recorded for audit purposes
                  </small>
                </div>

                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong>Important:</strong>
                  This will extend the deadline for all doctors assigned to this batch.
                  They will be notified of the new deadline automatically.
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowExtendModal(false)}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-warning"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Extending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-clock me-2"></i>
                        Extend Deadline
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </Modal>
        
        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete Batch"
          size="md"
        >
          {deletingBatch && (
            <div>
              <div className="alert alert-danger">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>Warning:</strong> This action cannot be undone!
              </div>
              
              <p>Are you sure you want to delete the batch <strong>"{deletingBatch.name}"</strong>?</p>
              
              <div className="card bg-light mb-3">
                <div className="card-body">
                  <div className="row">
                    <div className="col-6">
                      <small className="text-muted">Cases:</small>
                      <div className="fw-bold">{deletingBatch.case_count || 0}</div>
                    </div>
                    <div className="col-6">
                      <small className="text-muted">Doctors:</small>
                      <div className="fw-bold">{deletingBatch.doctor_count || 0}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="text-muted small">
                <i className="fas fa-info-circle me-1"></i>
                Deleting this batch will also remove:
              </p>
              <ul className="text-muted small">
                <li>All case assignments to this batch</li>
                <li>All doctor assignments to this batch</li>
                <li>All protocol assignments to this batch</li>
                <li>All work progress records for this batch</li>
              </ul>
              
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash me-2"></i>
                      Delete Batch
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </ResearchLayout>
  );
};

export default MedicalAdminBatchCreation;